import React from 'react';
import { Trash2, Play, Mail, Phone, Plus } from 'lucide-react';
import { Trigger } from '../../lib/automations/triggers';
import { Action } from '../../lib/automations/actions';

interface ResponsiveWorkflowProps {
  triggers: Trigger[];
  actions: Action[];
  onTriggerSettingsClick: (trigger: Trigger) => void;
  onActionSettingsClick: (action: Action) => void;
  onTestTrigger: (trigger: Trigger) => void;
  onRemoveTrigger: (triggerId: string) => void;
  onRemoveAction: (actionId: string) => void;
  onAddClick: () => void;
}

export default function ResponsiveWorkflow({
  triggers,
  actions,
  onTriggerSettingsClick,
  onActionSettingsClick,
  onTestTrigger,
  onRemoveTrigger,
  onRemoveAction,
  onAddClick
}: ResponsiveWorkflowProps) {
  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Triggers */}
      {triggers.map((trigger, triggerIndex) => (
        <div key={trigger.id} className="relative">
          <div 
            className="bg-yellow-50 rounded-lg border border-yellow-200 p-4 w-full max-w-md mx-auto shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => trigger.settings && onTriggerSettingsClick(trigger)}
            style={{ cursor: trigger.settings ? 'pointer' : 'default' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 flex-shrink-0 rounded-full bg-yellow-400"></div>
                  <h4 className="text-sm font-medium text-yellow-900 truncate">{trigger.name}</h4>
                </div>
                <p className="text-sm text-yellow-700 mt-1 ml-4 line-clamp-2">{trigger.description}</p>
                {trigger.settings && (
                  <div className="mt-2 ml-4 space-y-1">
                    {trigger.settings.phoneNumber && (
                      <div className="flex items-center space-x-2 text-sm text-yellow-700">
                        <Phone className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{trigger.settings.phoneNumber}</span>
                      </div>
                    )}
                    {trigger.settings.email && (
                      <div className="flex items-center space-x-2 text-sm text-yellow-700">
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{trigger.settings.email}</span>
                      </div>
                    )}
                    {trigger.settings.reminderHours && trigger.id === 'appointment_reminder' && (
                      <div className="text-sm text-yellow-700">
                        <span className="truncate">
                          Reminder{trigger.settings.reminderHours.length > 1 ? 's' : ''}: {trigger.settings.reminderHours.map(h => `${h}h`).join(', ')} before
                        </span>
                      </div>
                    )}
                    {trigger.settings.statusValues && trigger.id === 'booking_status' && (
                      <div className="text-sm text-yellow-700">
                        <span className="truncate">
                          Status: {(trigger.settings.statusValues as string[])[0] === 'in_progress' ? 'In Progress' : 'Completed'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTestTrigger(trigger);
                  }}
                  className="p-1.5 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-100 rounded-md transition-colors"
                  title="Test trigger"
                  style={{ display: trigger.id === 'appointment_reminder' || trigger.id === 'scheduled_trigger' || trigger.id === 'appointment_canceled' || trigger.id === 'booking_status' ? 'none' : 'block' }}
                >
                  <Play className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveTrigger(trigger.id);
                  }}
                  className="p-1.5 text-yellow-700 hover:text-red-600 hover:bg-yellow-100 rounded-md transition-colors"
                  title="Remove trigger"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Only add dotted line if it's not the last trigger and there are no actions */}
          {(triggerIndex < (triggers.length - 1) || actions.length > 0) && (
            <div className="flex justify-center my-2">
              <div className="h-8 border-l-2 border-dotted border-gray-400"></div>
            </div>
          )}
        </div>
      ))}

      {/* Actions */}
      {triggers.length > 0 && actions.length > 0 && (
        <h3 className="text-lg font-medium text-blue-900 text-center">Actions</h3>
      )}
      
      {actions.map((action, index) => (
        <div key={action.id} className="relative">
          <div 
            className="bg-blue-50 rounded-lg border border-blue-200 p-4 w-full max-w-md mx-auto shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onActionSettingsClick(action)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 flex-shrink-0 rounded-full bg-blue-400"></div>
                  <h4 className="text-sm font-medium text-blue-900 truncate">{action.name}</h4>
                </div>
                <p className="text-sm text-blue-700 mt-1 ml-4 line-clamp-2">{action.description}</p>
                {action.settings && (
                  <div className="mt-2 ml-4 space-y-1">
                    {action.settings.phoneNumber && (
                      <div className="flex items-center space-x-2 text-sm text-blue-700">
                        <Phone className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{action.settings.phoneNumber}</span>
                      </div>
                    )}
                    {action.settings.message && (
                      <div className="flex items-start space-x-2 text-sm text-blue-700">
                        <Mail className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{action.settings.message}</span>
                      </div>
                    )}
                    {action.settings.subject && (
                      <div className="flex items-center space-x-2 text-sm text-blue-700">
                        <span className="font-medium flex-shrink-0">Subject:</span>
                        <span className="truncate">{action.settings.subject}</span>
                      </div>
                    )}
                    {action.settings.emailBody && (
                      <div className="flex items-start space-x-2 text-sm text-blue-700">
                        <span className="font-medium flex-shrink-0 mt-0.5">Body:</span>
                        <span className="line-clamp-1">{action.settings.emailBody}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveAction(action.id);
                  }}
                  className="p-1.5 text-blue-700 hover:text-red-600 hover:bg-blue-100 rounded-md transition-colors"
                  title="Remove action"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Dotted line after each action except the last one */}
          {index < actions.length - 1 && (
            <div className="flex justify-center my-2">
              <div className="h-8 border-l-2 border-dotted border-gray-400"></div>
            </div>
          )}
        </div>
      ))}

      {/* Add Button */}
      <div className="flex justify-center mt-6">
        {triggers.length || actions.length ? (
          <button
            onClick={onAddClick}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors border-2 border-dashed border-gray-300"
          >
            <Plus className="h-5 w-5 text-gray-800" />
          </button>
        ) : (
          <button
            onClick={onAddClick}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border-2 border-dashed border-gray-300 text-gray-800 hover:border-gray-400 hover:bg-gray-100 focus:outline-none"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Trigger
          </button>
        )}
      </div>
    </div>
  );
} 